import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Separator,
  Tooltip
} from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { CREATE_APP_API_KEY } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type CreateAppAPIKeyInput = {
  appType: AppTypeEnum
  source: AppTypeEnum
}

/**
 * This is the modal for the CreateAppAPIKeyModal app.
 * It is used to create an API key for an app.
 */
export function CreateAppAPIKeyModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [generatedApiKey, setGeneratedApiKey] = useState('')

  const [createAppAPIKey, { loading }] = useMutation<{ createAppAPIKey: { value: string } }, CreateAppAPIKeyInput>(
    CREATE_APP_API_KEY,
    {
      context: {
        headers: {
          'ws-id': workspaceID
        }
      },
      onCompleted: (result) => {
        setGeneratedApiKey(result.createAppAPIKey.value)
        toast.success('API Key generated successfully!')
      },
      onError: (err) => {
        const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        setFormError(errorMessage)
        toast.error(errorMessage)
      },
      refetchQueries: [GET_APPS]
    }
  )

  const handleClose = () => {
    setFormError('')
    setGeneratedApiKey('')
    onClose()
  }

  const handleCreateAPIKey = () => {
    createAppAPIKey({
      variables: {
        appType: appType as AppTypeEnum,
        source: appType as AppTypeEnum
      }
    })
  }

  const displayedApiKey = generatedApiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {!displayedApiKey ? `Enable ${app?.appName} for Chain` : `${app?.appName} is enabled for your workspace`}
          </DialogTitle>
          <DialogDescription>
            {!displayedApiKey
              ? `You must generate an API key and provide it to ${app?.appName} support.`
              : 'An API Key has been generated for this app.'}
          </DialogDescription>
        </DialogHeader>

        {!!formError && <Alert variant="error" description={formError} />}

        {!!displayedApiKey && (
          <>
            <Separator className="my-4" />
            <div className="space-y-2">
              <p className="text-sm font-medium">Chain API Token:</p>
              <div className="relative">
                <div className="rounded-md border bg-muted px-3 py-2 font-mono text-sm">{displayedApiKey}</div>
                <div className="absolute right-2 top-2">
                  <Tooltip content="Copy to clipboard">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(displayedApiKey)
                        toast.success('API Key copied to clipboard!')
                      }}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </div>
            <Separator className="my-4" />
          </>
        )}

        <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
          {!!app?.docs && (
            <a
              href={app.docs}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Read the integration guide
              <ExternalLink className="h-4 w-4" />
            </a>
          )}

          <div className="space-x-2">
            <Button variant="outline" onClick={handleClose} type="button">
              Cancel
            </Button>
            {!displayedApiKey && (
              <Button type="button" onClick={handleCreateAPIKey} disabled={loading}>
                {loading ? 'Generating...' : 'Generate Key'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
